// ---- NPC system ----
// Standing characters with canned, proximity-triggered dialogue. Parallel to the
// `deco` (scatter props) and `structure` (walkable buildings) systems, but an NPC
// adds *behavior*: a floating nameplate, an optional collider so you bump into it,
// and a comic speech bubble that pops over its head when the player gets close.
//
// An NPC is authored in LOCAL coords (feet at y=0) via the meshkit prims, exactly
// like a deco/structure, then placed at a world spot. Add one by writing a file
// (see king.ts) and registering it in index.ts — the manager wires up the mesh,
// nameplate, collider, and bubble for every NPC uniformly.

import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { makePrims, SOFT_BOX, SHARED_GEOS, type Prims } from "../meshkit.ts";
import { TOON_GRADIENT, TOON_SMOOTH, toonify } from "../toon.ts";
import { bakePrim } from "../instanced.ts";
import type { Collider } from "../physics.ts";
import type { ItemType } from "../shared.ts";
import type { LootTable } from "../loot.ts";
import type { CombatEffect } from "../sim/status.ts";

export type NpcSpec = {
  /** Stable id (salts mesh variation; handy for debugging). */
  id: string;
  /** The content DOC id this spawn expanded from (content/npcs/<kind>.json) —
   *  what scenarios and tooling mean by "an NPC kind". Spawn ids stay the
   *  persistence keys; several spawns share one kind. */
  kind?: string;
  /** Shown on the floating nameplate. */
  name: string;
  /** World position of the NPC's feet. `y` is snapped to the actual floor/terrain
   *  under (x,z) on init (and continuously while roaming), so it only needs to be
   *  a ballpark — close enough to pick the right floor for a stacked structure
   *  (e.g. ~10 for a castle rampart, ~0 for ground); the exact grade is resolved. */
  x: number;
  y: number;
  z: number;
  /** Facing angle in radians (atan2(wx, wz) convention; 0 faces +z / south). */
  facing?: number;
  /** Authored idle body pose (content/anim/clips.json `poses`) — a seated
   *  innkeeper. Static NPCs hold it client-side; dynamic ones get it streamed
   *  (NetNpc.pose) and stand up for good the moment they move/fight/flee. */
  pose?: string;
  /** Canned lines, cycled one per re-entry into range. */
  dialogue: string[];
  /** Show a floating name tag above the head. Default true; creatures opt out. */
  nameplate?: boolean;
  /** How close (world units, on the xz plane) the player must be. Default 6. */
  radius?: number;
  /** Height above the feet to anchor the speech bubble. Default 3. */
  height?: number;
  /** Emit a circle collider so players can't walk through. Default true. */
  solid?: boolean;
  /** Collider radius / height above feet (only when solid). Default 0.6 / 2.2. */
  colliderR?: number;
  colliderH?: number;
  /** If set, the NPC wanders between random waypoints inside these xz bounds.
   *  Bounds must stay over walkable floor — height is re-resolved each frame, so
   *  wandering off an edge would make it fall. Feet glide; a gentle bob fakes the
   *  walk (prim NPCs have no skeletal rig). */
  roam?: {
    minX: number; maxX: number; minZ: number; maxZ: number;
    speed?: number;    // units/sec (default 1.2)
    pauseMin?: number; // seconds idle at a waypoint (default 1)
    pauseMax?: number; // (default 3)
  };
  /** Make this NPC fightable. Presence of this block is the on-switch — omit it
   *  and the NPC can be focused but never engages / takes damage. See combat.ts. */
  combat?: CombatSpec;
  /** Faction tag (plan 14) — who this NPC sides with/against, resolved through the
   *  content stance table (content/world/factions.json). Omit = unaligned (neutral
   *  to everyone). `player` is the implicit player-side faction id. */
  faction?: string;
  /** Territorial aggro (plan 14). Presence is the on-switch (mirrors combat/talk/
   *  flee): hostiles inside `radius` of the spawn point are attacked unprovoked;
   *  the chase gives up past `leash` from home, then it walks back and heals to
   *  full (the sim's leash-return state — see src/sim/combat.ts selectTarget). */
  territory?: TerritorySpec;
  /** How far (xz units) this NPC scans for embattled faction-mates to assist
   *  (plan 14 assistScan). Defaults from its faction's doc entry. */
  assistRadius?: number;
  /** Make this NPC talkable. Presence of this block is the on-switch (mirrors
   *  `combat`) — focusing a talkable NPC offers a Talk action that opens a
   *  branching conversation. Omit it and the NPC only shows its proximity bubble.
   *  See talk.ts. */
  talk?: TalkSpec;
  /** Make this NPC skittish wildlife. Presence of this block is the on-switch
   *  (mirrors `combat`/`talk`) and implies the NPC is dynamic. The server makes it
   *  bolt directly away from any nearby player at `fleeSpeed`, then calm down and
   *  resume roaming once everyone is past `safeRadius` — so it needs a `roam` block
   *  to settle back into. Flee ALWAYS wins over the combat AI: a creature with both
   *  `flee` and `combat` is prey — it can be hunted/defeated but never chases or
   *  punches; being hit just spooks it. See index.ts `stepNpc`. */
  flee?: FleeSpec;
  /** Drops this NPC yields when defeated. Its own data block (mirrors
   *  `combat`/`talk`/`flee`) — an NPC drops loot iff this is present. The server
   *  rolls the table on defeat (each entry an independent `chance`) and spawns the
   *  winners as ordinary world items where the NPC fell. See src/loot.ts +
   *  index.ts `defeatNpc`. Requires `combat` to actually be defeatable. */
  loot?: LootTable;
  /** The model doc/coded id this NPC renders from (content `model` ref). Carried
   *  through so tooling (the studio catalog) can look the model's archetype up to
   *  attach the shared gait animator; the game just uses `build`. */
  model?: string;
  /** Build the mesh into the group via meshkit prims (feet at y=0). */
  build: (p: Prims) => void;
  /** Dev-studio only (src/studio): optional per-frame animation for the model
   *  viewer, receiving the built+normalized group. Ignored by the game and the
   *  headless server. NPCs are rigless prim bodies today, so they're static in the
   *  studio by default; set this when a model gains its own animation. */
  studioAnimate?: (o: THREE.Object3D, dt: number, t: number) => void;
};

/** Skittish-wildlife tuning. Every field is optional — `defineCreature` fills in
 *  shared `WILDLIFE_FLEE` defaults, so a species spec only overrides what differs.
 *  The server reads the finished values (with its own `??` fallbacks for safety). */
export type FleeSpec = {
  /** A WALKING player within this xz distance (world units) starts the flee. Default 9. */
  alertRadius?: number;
  /** `alertRadius` is multiplied by this when the threatening player is RUNNING, so a
   *  runner spooks it from farther out. Default 1.7. */
  runAlertMultiplier?: number;
  /** It only calms once the nearest player is beyond this. MUST be > the effective
   *  alert radius — the gap is the hysteresis band that stops flicker. Default 16. */
  safeRadius?: number;
  /** Bolt speed (units/sec). Set above the player's RUN_SPEED (7.6) so it gets away
   *  in a straight line but stays catchable on corners. Default 8.4. */
  fleeSpeed?: number;
  /** Minimum seconds it commits to bolting after a spook, even if the player backs
   *  off immediately (anti-flicker; refreshed while the player stays in range). Default 1.2. */
  panicMin?: number;
  /** Max radians/sec the flee heading may swing, so a player circling it doesn't cause
   *  a 1-frame 180° snap. Default 6 (≈ near-instant but smoothed). */
  turnRate?: number;
};

/** Territorial-aggro tuning (plan 14). Distances are xz world units from the
 *  NPC's home point (its spawn; re-homed when wildlife respawns elsewhere). */
export type TerritorySpec = {
  /** Hostiles inside this radius of home are attacked unprovoked. */
  radius: number;
  /** The chase gives up once the NPC is this far from home (then it leash-returns
   *  and heals). Keep > radius, or it drops targets it just acquired. */
  leash: number;
};

/** Per-NPC combat tuning. Only `hp`, `stamina`, and `aggressive` are required. */
export type CombatSpec = {
  /** Starting (and max) hitpoints. Each punch deals `attackDamage` (default 1). */
  hp: number;
  /** Starting (and max) stamina. Each punch it throws costs 1; regens over time. */
  stamina: number;
  /** Hostile temperament flag (studio badges / authoring). Focus never starts a
   *  fight — unprovoked chase needs territory + a hostile faction stance; a hit
   *  always provokes retaliation regardless of this flag. */
  aggressive: boolean;
  /** Melee reach on the xz plane (world units). Default 2.2. */
  attackRange?: number;
  /** HP removed per landed punch. Default 1. */
  attackDamage?: number;
  /** Seconds between its own punches. Default 1.5. */
  attackCooldown?: number;
  /** Chase speed while pursuing the player (units/sec). Default 2.2. */
  pursueSpeed?: number;
  /** Seconds until this NPC respawns after defeat. Omit → RESPAWN_DELAY (60)
   *  in src/sim/combat.ts. Variants deep-merge (e.g. scrawny-wolf inherits wolf). */
  respawn?: number;
  /** Out-of-combat HP regen (points/sec). Omit → NPC_HP_REGEN (1). Explicit 0
   *  disables. Only while no combat target (and not defeated). Variants deep-merge. */
  hpRegen?: number;
  /** Presence = ARCHER (plan 14): holds range and fires projectiles at the
   *  target (distance→hit%% bands, ammo-free), melee fallback when closed on. */
  ranged?: {
    bands: { max: number; hit: number }[];
    projectileSpeed: number;
  };
  /** Crowd-control applied on each landed melee hit (snare / stun / knockback). */
  effects?: CombatEffect[];
};

// ---- Talk / dialogue ------------------------------------------------------
// A `talk` block is a branching conversation graph, authored as plain data the
// same way `combat` is. It's the foundation for richer interactions (quests,
// shops, lore): a node carries the NPC's line(s) plus the player's response
// choices, and each choice can route to another node AND/OR fire an `action`.
// Both client (talk.ts) and the Three-free server import this, so keep it DOM-
// and THREE-free.

/** An optional side effect fired when a choice is selected. The dispatcher
 *  (talk.ts `applyTalkAction`) is stubbed for now — unimplemented kinds log —
 *  so quests/shops/etc. drop in later without reworking the conversation model. */
export type TalkAction =
  | { kind: "startQuest"; id: string }
  | { kind: "advanceQuest"; id: string }
  | { kind: "giveItem"; type: ItemType }
  | { kind: "openShop"; id: string }
  | { kind: "playCinematic"; id: string }
  | { kind: "custom"; tag: string };

/** A data predicate that gates a dialogue choice on the player's quest state, so one
 *  conversation can branch across a quest's life (offer → in-progress → turn-in →
 *  done). Kept as plain data (not a function) so the server-imported module stays clean;
 *  evaluated client-side by talk.ts against src/quest.ts state (+ bag for `has`). */
export type TalkCond = {
  /** Quest id this condition reads. */
  quest: string;
  /** Required state: not yet taken / underway / finished. */
  is: "unstarted" | "active" | "completed";
  /** When `is:"active"`, also require this to be the current step. */
  step?: string;
  /** Optional bag gate: only show when the bag holds `count` (default 1) of ANY
   *  listed type (OR-sum, same as quest `item`/`deliver`). Use on turn-in lines
   *  so "I brought you a fish" doesn't appear bare-handed. */
  has?: { types: string[]; count?: number };
};

export type TalkChoice = {
  /** The player's response, shown on the player side of the dialogue HUD. */
  text: string;
  /** Node id to advance to. Omit (or point at no node) to end the conversation. */
  to?: string;
  /** Optional hook fired when this choice is picked. */
  action?: TalkAction;
  /** Show this choice only when the player's quest state matches. Omit = always shown. */
  when?: TalkCond;
};

export type TalkNode = {
  /** Unique within this conversation; `TalkSpec.root` names the first one. */
  id: string;
  /** The NPC's line(s) for this node (a string, or several shown together). */
  npc: string | string[];
  /** The player's response options. If omitted, a single "Continue" ends the talk. */
  choices?: TalkChoice[];
  /**
   * Optional ENTRY gate — when set and `condMet`, this node becomes the talk
   * open (first matching node in list order wins over `TalkSpec.root`).
   * Use for quest check-ins: "Got my logs yet?" while the deliver step is active.
   */
  when?: TalkCond;
};

export type TalkSpec = {
  /** How close (xz world units) the player must be to start. Default spec.radius ?? 6. */
  range?: number;
  /** The starting node's id. */
  root: string;
  /** The conversation graph (looked up by node id). */
  nodes: TalkNode[];
};

/**
 * Pick the conversation open: first node whose `when` matches, else `talk.root`.
 * Shared by the game client and the studio Dialogue preview.
 */
export function resolveTalkRoot(
  talk: TalkSpec,
  met: (cond: TalkCond) => boolean,
): string {
  for (const n of talk.nodes) {
    if (n.when && met(n.when)) return n.id;
  }
  return talk.root;
}

export function defineNpc(spec: NpcSpec): NpcSpec {
  return spec;
}

/** Whether a spec offers a conversation (mirror of the `combat` → fightable test). */
export function isTalkable(spec: NpcSpec): boolean {
  return !!spec.talk;
}

/** Find a conversation node by id (linear scan — graphs are tiny). */
export function talkNode(talk: TalkSpec, id: string): TalkNode | undefined {
  return talk.nodes.find((n) => n.id === id);
}

/** Whether this NPC's conversation can open the given shop (an `openShop` talk
 *  action for `shopId`). The server uses it to authorize a buy/sell against the
 *  keeper the player is actually talking to, so a shop isn't pinned to one NPC
 *  spawn id — the same keeper kind spawns under different ids per map (`cook`
 *  vs `tut-cook`), and both must trade. Graphs are tiny; scan them whole. */
export function opensShop(spec: NpcSpec, shopId: string): boolean {
  for (const node of spec.talk?.nodes ?? [])
    for (const c of node.choices ?? [])
      if (c.action?.kind === "openShop" && c.action.id === shopId) return true;
  return false;
}

/** Whether this NPC's conversation can gift the given item type (a `giveItem`
 *  talk action). Server `acceptGift` authorizes against the live talk lock +
 *  this graph scan — same pattern as `opensShop`, so gifts aren't pinned to a
 *  single spawn id. */
export function offersGift(spec: NpcSpec, type: ItemType): boolean {
  for (const node of spec.talk?.nodes ?? [])
    for (const c of node.choices ?? [])
      if (c.action?.kind === "giveItem" && c.action.type === type) return true;
  return false;
}

export type BuiltNpc = { group: THREE.Group; collider?: Collider };

// ---- Rig mesh consolidation (FPS pass, 2026-07-15) --------------------------
// A rig is emitted one Mesh per prim — dozens of draw calls (each also a shadow
// caster with its own per-color material) per character, which was the deco
// system's pre-bake state. Every mesh directly under a given Group is RIGID
// relative to it (animation rotates the limb GROUPS, never a mesh inside one),
// so after build each group's plain-toon Mesh children merge into at most two
// vertex-colored meshes (chunky + smooth ramp — the bakeDeco recipe), sharing
// ONE material pair across every rig in the world. Joints, sockets, and nested
// groups are untouched — the rig contract, gait, and dressing keep working —
// and runtime attachments (weapons, dress parts, nameplates) arrive AFTER this
// pass, staying separate on purpose. Snapshot note: this changes per-mesh
// signatures (test/fixtures/model-baseline.json re-baselined with it); each
// entry's whole-model AABB is the parity check that survives the merge.
const RIG_CHUNKY = toonify(new THREE.MeshToonMaterial({ vertexColors: true, gradientMap: TOON_GRADIENT }));
const RIG_SMOOTH = toonify(new THREE.MeshToonMaterial({ vertexColors: true, gradientMap: TOON_SMOOTH }));

function mergeRigidParts(root: THREE.Group): void {
  // Geometry-usage census FIRST: mirror/repeat part modifiers reuse ONE
  // geometry object across several meshes, so a bake must always COPY (an
  // in-place transform would double-apply on the geometry's second user) and
  // an original may only be disposed once EVERY user of it got merged.
  const users = new Map<THREE.BufferGeometry, number>();
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) users.set(m.geometry, (users.get(m.geometry) ?? 0) + 1);
  });
  const bakedUses = new Map<THREE.BufferGeometry, number>();

  const groups: THREE.Object3D[] = [];
  root.traverse((o) => {
    if ((o as THREE.Group).isGroup) groups.push(o);
  });
  for (const g of groups) {
    const chunky: THREE.BufferGeometry[] = [];
    const smooth: THREE.BufferGeometry[] = [];
    const victims: THREE.Mesh[] = [];
    for (const c of g.children) {
      const m = c as THREE.Mesh;
      // Merge only anonymous, leaf, opaque, solid-color toon prims — the
      // overwhelming bulk of a rig. Anything special (transparent, already
      // vertex-colored, named/tagged for later reference, hosting a subtree)
      // stays its own mesh.
      if (!m.isMesh || m.children.length) continue;
      const mat = m.material as THREE.MeshToonMaterial;
      if (Array.isArray(m.material) || !mat.isMeshToonMaterial) continue;
      if (mat.transparent || mat.vertexColors) continue;
      if (m.name || Object.keys(m.userData).length) continue;
      // copy:true — the census above proves rigs SHARE geometry (mirror/repeat),
      // so the bake must never transform in place; disposal happens below once
      // every user of a geometry has merged.
      const geo = bakePrim(m, true);
      (mat.gradientMap === TOON_SMOOTH ? smooth : chunky).push(geo);
      bakedUses.set(m.geometry, (bakedUses.get(m.geometry) ?? 0) + 1);
      victims.push(m);
    }
    if (!victims.length) continue;
    for (const m of victims) g.remove(m);
    for (const [parts, mat] of [[chunky, RIG_CHUNKY], [smooth, RIG_SMOOTH]] as const) {
      if (!parts.length) continue;
      const merged = new THREE.Mesh(mergeGeometries(parts, false)!, mat);
      parts.forEach((p) => p.dispose()); // mergeGeometries copies — free the intermediates
      merged.castShadow = true;
      merged.receiveShadow = false; // the push() convention below
      g.add(merged);
    }
  }

  // Free originals now fully replaced (never process-shared unit geos; a
  // geometry still referenced by a kept mesh keeps living).
  for (const [g, n] of bakedUses) {
    if (SHARED_GEOS.has(g)) continue;
    if (users.get(g) === n) g.dispose();
  }
}

/** Assemble an NPC mesh group (feet at origin): the shadow flags + seed
 *  convention in ONE place — used by buildNpc and the studio's live-rebuild
 *  preview (views/models.ts), so the two can't drift. */
export function npcMeshGroup(id: string, build: (p: Prims) => void): THREE.Group {
  const group = new THREE.Group();
  const push = (m: THREE.Mesh) => {
    m.castShadow = true;
    m.receiveShadow = false; // don't get striped by wall shadows indoors (see player.ts)
    group.add(m);
  };
  build(makePrims(push, id.length + 11, SOFT_BOX)); // characters share the player's soft prim
  mergeRigidParts(group);
  return group;
}

/** Run an NPC's build() and return its mesh (placed at the world spot) + collider. */
export function buildNpc(spec: NpcSpec): BuiltNpc {
  const group = npcMeshGroup(spec.id, spec.build);

  group.position.set(spec.x, spec.y, spec.z);
  if (spec.facing !== undefined) group.rotation.y = spec.facing;

  let collider: Collider | undefined;
  if (spec.solid !== false) {
    collider = {
      x: spec.x,
      z: spec.z,
      r: spec.colliderR ?? 0.6,
      minY: spec.y,
      maxY: spec.y + (spec.colliderH ?? 2.2),
    };
  }
  return { group, collider };
}
